import { MenuNavLink } from '@/components/MainPageLink';
import TxAnchor from '@/components/TxAnchor';
import { useAdminPerms } from '@/hooks/auth';
import { txConfigStateAtom } from '@/hooks/status';
import { TxConfigState } from '@shared/enums';
import { useAtomValue } from 'jotai';
import {
    ActivityIcon,
    BoxesIcon,
    ClipboardListIcon,
    FolderOpenIcon,
    HistoryIcon,
    HourglassIcon,
    LayoutDashboardIcon,
    ListChecksIcon,
    ScrollTextIcon,
    SettingsIcon,
    ShieldCheckIcon,
    ShieldOffIcon,
    TerminalSquareIcon,
    TrendingDownIcon,
    UsersIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';

type PendingServerConfigureProps = {
    txConfigState?: Exclude<TxConfigState, TxConfigState.Ready>;
}

function PendingServerConfigure({ txConfigState }: PendingServerConfigureProps) {
    const [currLocation] = useLocation();
    const [linkHref, setLinkHref] = useState('');
    const linkText = useRef('');

    useEffect(() => {
        let newHref = '';
        if (txConfigState === TxConfigState.Setup && !currLocation.startsWith('/server/setup')) {
            newHref = '/server/setup';
            linkText.current = 'Go to the setup page!';
        } else if (txConfigState === TxConfigState.Deployer && !currLocation.startsWith('/server/deployer')) {
            newHref = '/server/deployer';
            linkText.current = 'Go to the deployer page!';
        } else {
            newHref = '';
        }

        if (!newHref) {
            setLinkHref('');
            return;
        } else {
            const timeout = setTimeout(() => {
                setLinkHref(newHref);
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [currLocation, txConfigState]);

    return (
        <div className='absolute inset-0 flex flex-col items-center justify-center gap-4 z-10 rounded-xl'
            style={{ background: 'var(--sc-bg-surface)' }}>
            <HourglassIcon className='h-12 w-12 opacity-75 animate-pulse' style={{ color: 'var(--sc-cyan)' }} />
            <p className='text-center text-sm tracking-wider font-light opacity-75 px-4'>
                You need to configure your server to be able to start it.
            </p>
            {linkHref ? (
                <TxAnchor href={linkHref} className='animate-toastbar-enter'>
                    {linkText.current}
                </TxAnchor>
            ) : (
                <TxAnchor href='#' className='animate-toastbar-leave pointer-events-none'>
                    {linkText.current || <>&nbsp;</>}
                </TxAnchor>
            )}
        </div>
    );
}

function NavGroup({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="mb-2">
            <div
                className="px-3 mb-1 text-[9px] font-semibold tracking-[0.18em] uppercase"
                style={{ color: 'rgba(0,255,209,0.25)' }}
            >
                {label}
            </div>
            <div className="space-y-0.5">{children}</div>
        </div>
    );
}

export default function ServerMenu() {
    const txConfigState = useAtomValue(txConfigStateAtom);
    const { hasPerm } = useAdminPerms();
    const isReady = txConfigState === TxConfigState.Ready;

    return (
        <div className='relative'>
            {!isReady && <PendingServerConfigure txConfigState={txConfigState} />}
            <nav
                className='select-none px-2 py-1'
                style={!isReady ? { opacity: 0, pointerEvents: 'none' } : undefined}
            >
                <NavGroup label="Server">
                    <MenuNavLink href="/" disabled={!isReady}>
                        <LayoutDashboardIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Dashboard
                    </MenuNavLink>
                    <MenuNavLink href="/server/console" disabled={!isReady || !hasPerm('console.view')}>
                        <TerminalSquareIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Live Console
                    </MenuNavLink>
                    <MenuNavLink href="/server/resources" disabled={!isReady}>
                        <BoxesIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Resources
                    </MenuNavLink>
                </NavGroup>

                <NavGroup label="Manage">
                    <MenuNavLink href="/players" disabled={!isReady}>
                        <UsersIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Players
                    </MenuNavLink>
                    <MenuNavLink href="/whitelist">
                        <ListChecksIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Whitelist
                    </MenuNavLink>
                    <MenuNavLink href="/files">
                        <FolderOpenIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        File Manager
                    </MenuNavLink>
                </NavGroup>

                <NavGroup label="System">
                    <MenuNavLink href="/settings" disabled={!hasPerm('settings.view')}>
                        <SettingsIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Settings
                    </MenuNavLink>
                    <MenuNavLink href="/admins">
                        <ShieldCheckIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Admins
                    </MenuNavLink>
                    <MenuNavLink href="/settings/ban-templates">
                        <ShieldOffIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Ban Templates
                    </MenuNavLink>
                </NavGroup>

                <NavGroup label="Logs">
                    <MenuNavLink href="/history">
                        <HistoryIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        History
                    </MenuNavLink>
                    <MenuNavLink href="/system/console-log" disabled={!hasPerm('txadmin.log.view')}>
                        <ScrollTextIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Console Log
                    </MenuNavLink>
                    <MenuNavLink href="/system/action-log" disabled={!hasPerm('txadmin.log.view')}>
                        <ClipboardListIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Action Log
                    </MenuNavLink>
                    <MenuNavLink href="/audit">
                        <ActivityIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Audit Trail
                    </MenuNavLink>
                    <MenuNavLink href="/insights/player-drops">
                        <TrendingDownIcon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                        Player Drops
                    </MenuNavLink>
                </NavGroup>
            </nav>
        </div>
    );
}
